import { getTodayString } from './dateHelpers';

export type TimeFilter = 'all' | 'future' | 'past';

export interface HistoryEvent {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  spots: number;
  fee: number;
  checkedIn: boolean;
  paid: boolean;
  approved: boolean;
  joinedAt: string;
  sendDetails?: string;
  otherDetails?: string;
  totalParticipants?: number;
  maxParticipants?: number;
  hasAnnouncements?: boolean;
  announcementCount?: number;
  paymentLink?: string;
}

/**
 * Filter events by time period
 * @param events - Array of events
 * @param filter - Time filter type
 * @returns Filtered events
 */
export const filterEventsByTime = (
  events: HistoryEvent[], 
  filter: TimeFilter
): HistoryEvent[] => {
  const today = getTodayString();
  
  switch (filter) {
    case 'future':
      return events.filter(event => event.eventDate >= today);
    case 'past':
      return events.filter(event => event.eventDate < today);
    default:
      return events;
  }
};

/**
 * Sort events by date
 * Past events: newest first, Future events: oldest first
 * @param events - Array of events
 * @returns Sorted events
 */
export const sortEvents = (events: HistoryEvent[]): HistoryEvent[] => {
  const today = getTodayString();
  
  return events.sort((a, b) => {
    const dateA = new Date(a.eventDate).getTime();
    const dateB = new Date(b.eventDate).getTime();
    const isPastA = a.eventDate < today;
    const isPastB = b.eventDate < today;
    
    // Past events: newest first, Future events: oldest first
    if (isPastA && isPastB) {
      return dateB - dateA;
    }
    return dateA - dateB;
  });
};

/**
 * Paginate events array
 * @param events - Array of events
 * @param page - Page number (1-indexed)
 * @param perPage - Items per page
 * @returns Paginated events
 */
export const paginateEvents = (
  events: HistoryEvent[], 
  page: number, 
  perPage: number
): HistoryEvent[] => {
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  return events.slice(startIndex, endIndex);
};

/**
 * Calculate total number of pages
 * @param totalItems - Total number of items
 * @param perPage - Items per page
 * @returns Total pages
 */
export const calculateTotalPages = (totalItems: number, perPage: number): number => {
  return Math.ceil(totalItems / perPage);
};

/**
 * Check if event needs approval
 * @param event - Event object
 * @returns True if event needs approval
 */
export const needsApproval = (event: HistoryEvent): boolean => {
  return !event.approved;
};

/**
 * Check if event is unpaid
 * @param event - Event object
 * @returns True if event is unpaid
 */
export const isUnpaid = (event: HistoryEvent): boolean => {
  return event.approved && !event.paid;
};

/**
 * Check if event is paid
 * @param event - Event object
 * @returns True if event is paid
 */
export const isPaid = (event: HistoryEvent): boolean => {
  return event.paid;
};

/**
 * Check if event is checked in
 * @param event - Event object
 * @returns True if event is checked in
 */
export const isCheckedIn = (event: HistoryEvent): boolean => {
  return event.checkedIn;
};
